import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {PaymentsSearchParams} from '@features/payments/types.ts'
import {useTranslation} from '@/translations/i18n.tsx'
import {formatLocaleDate} from '@/dates.ts'
import {endOfDay, isSameDay, startOfDay, startOfYear, subMonths, subWeeks, subYears} from 'date-fns'
import {formatPaymentAmount} from '@features/payments/utils.ts'
import {useFreelancerPaymentsDates} from '@features/payments/queries/useFreelancerPaymentsDates.ts'
import {Skeleton} from '@components/ui/skeleton'
import {FilterDatePicker} from '@features/payments/components/filter-date-picker'
import {cn} from '@utilities/helpers'

type SelectedFilters = 'last_week' | 'last_month' | 'year_to_date' | 'last_year' | 'custom'

const filterItemClass = (isSelected: boolean) =>
    cn(
        'cursor-pointer rounded-[18px] border border-neutral-300 px-4 py-2 text-sm/5 font-medium text-neutral-700',
        isSelected && 'border-2 border-neutral-600 text-neutral-900'
    )

export const PaymentsFilters = () => {
    const {t} = useTranslation()
    const {searchParams, setSearchParams} = useParsedSearchParams(PaymentsSearchParams)
    const freelancerPaymentsDateQuery = useFreelancerPaymentsDates()

    const paymentsFilterOptions = [
        {
            label: t('payments:last_week'),
            id: 'last_week',
            from: startOfDay(subWeeks(new Date(), 1)),
            to: endOfDay(new Date())
        },
        {
            label: t('home:your_work_section:last_month'),
            id: 'last_month',
            from: startOfDay(subMonths(new Date(), 1)),
            to: endOfDay(new Date())
        },
        {
            label: t('payments:year_to_date'),
            id: 'year_to_date',
            from: startOfYear(new Date()),
            to: endOfDay(new Date())
        },
        {
            label: t('payments:last_year'),
            id: 'last_year',
            from: startOfDay(subYears(new Date(), 1)),
            to: endOfDay(new Date())
        },
        {
            label: t('payments:custom'),
            id: 'custom',
            from: startOfDay(new Date()),
            to: endOfDay(new Date())
        }
    ] as const satisfies ReadonlyArray<{
        label: string
        id: SelectedFilters
        from: Date
        to: Date
    }>

    const selectedFilter: SelectedFilters =
        paymentsFilterOptions.find(
            option =>
                option.id != 'custom' &&
                isSameDay(option.from, searchParams.from) &&
                isSameDay(option.to, searchParams.to)
        )?.id ?? 'custom'

    const totalPaidOut = freelancerPaymentsDateQuery.totalPaidOut?.paid_out_amount ?? 0
    const totalPaidOutCurrency = freelancerPaymentsDateQuery.totalPaidOut?.paid_out_photographer_currency_amount ?? 0
    const type = totalPaidOut == 0 ? 'neutral' : totalPaidOut > 0 ? 'positive' : 'negative'
    const showTotalPaidOut =
        (freelancerPaymentsDateQuery?.remappedData.length > 0 && (totalPaidOutCurrency !== 0 || totalPaidOut !== 0)) ||
        freelancerPaymentsDateQuery.remappedData.length === 0

    return (
        <div className="flex w-full flex-col items-stretch justify-between gap-6 md:flex-row md:items-end">
            <div className="flex flex-wrap gap-2.5">
                {paymentsFilterOptions.map(item => {
                    const isSelected = item.id == selectedFilter
                    return item.id == 'custom' ? (
                        <FilterDatePicker
                            key={item.id}
                            values={[searchParams.from, searchParams.to]}
                            onConfirm={value => {
                                setSearchParams({
                                    from: value[0],
                                    to: value[1]
                                })
                            }}
                            calendarProps={{maxDate: new Date()}}
                            trigger={
                                <button className={filterItemClass(isSelected)}>
                                    {isSelected
                                        ? `${formatLocaleDate(searchParams.from, 'PP')} - ${formatLocaleDate(searchParams.to, 'PP')}`
                                        : item.label}
                                </button>
                            }
                        />
                    ) : (
                        <button
                            key={item.id}
                            className={filterItemClass(isSelected)}
                            onClick={() => {
                                setSearchParams({
                                    from: item.from,
                                    to: item.to
                                })
                            }}
                        >
                            {item.label}
                        </button>
                    )
                })}
            </div>
            {freelancerPaymentsDateQuery.isLoading ? (
                <Skeleton height={32} width={240} />
            ) : (
                showTotalPaidOut && (
                    <h3
                        className={cn(
                            'text-xl font-bold',
                            type == 'positive'
                                ? 'text-success-600'
                                : type == 'negative'
                                  ? 'text-danger-600'
                                  : 'text-neutral-900'
                        )}
                    >
                        {formatPaymentAmount({
                            amount: freelancerPaymentsDateQuery.totalPaidOut?.paid_out_photographer_currency_amount,
                            amountUsd: freelancerPaymentsDateQuery.totalPaidOut?.paid_out_amount ?? 0,
                            currency: freelancerPaymentsDateQuery.totalPaidOut?.paid_out_photographer_currency,
                            prefix: type == 'positive' ? '+' : ''
                        })}
                    </h3>
                )
            )}
        </div>
    )
}
